<template>
  <div class="hotel-facility-form">
    <b-card>
      <template #header>
        <h2 class="mb-0">{{ isEdit ? $t('hotel_facilities.edit_title') : $t('hotel_facilities.create_title') }}</h2>
      </template>
      <b-form @submit.prevent="handleSubmit" :validated="validated" novalidate>

        <!-- Hotel -->
        <b-form-group :label="$t('hotels.title')" label-for="hotel_id" :state="!hasError('hotel_id')"
          :invalid-feedback="getError('hotel_id')">
          <treeselect id="hotel_id" v-model="formFields.hotel_id" :options="hotelOptions" :state="!hasError('hotel_id')"
            :class="hasError('hotel_id') ? 'error-input' : ''" placeholder="Select Hotel"
            @input="removeError('hotel_id')">
          </treeselect>
        </b-form-group>

        <!-- Facilities -->
        <b-form-group :label="$t('hotel_facilities.form.facility_label')" label-for="facility_id"
          :state="!hasError('facility_id')" :invalid-feedback="getError('facility_id')">
          <treeselect
            v-model="formFields.facility_id"
            :options="facilityOptions"
            :multiple="true"
            :normalizer="normalizer"
            placeholder="Select Facilities"
          />
        </b-form-group>


        <div class="d-flex gap-2 mt-4">
          <b-button type="submit" variant="primary" :disabled="isSubmitting">
            <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
            {{ isSubmitting ? $t('common.saving') : $t('common.save') }}
          </b-button>
          <b-button variant="secondary" @click="cancel">{{ $t('common.cancel') }}</b-button>
        </div>

        <div v-if="formLevelError" class="mt-3">
          <b-alert show variant="danger" class="mb-0">
            {{ formLevelError }}
          </b-alert>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import NotifyMixin from "@/Util/NotifyMixin";
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';

export default {
  name: "HotelFacilityForm",
  mixins: [NotifyMixin],
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
  },
  components: {
    Treeselect,
  },
  data() {
    return {
      isSubmitting: false,
      validated: false,
      formErrors: {},
      formLevelError: "",
      formFields: {
        hotel_id: null,
        facility_id: [],
        is_active: 1,
      },
      hotelOptions: [],
      facilityOptions: [],
    };
  },
  computed: {
    isEdit() {
      return !!this.id;
    },
  },
  methods: {
    normalizer(node) {
      return {
        id: String(node.id),
        label: node.name
      };
    },
    hasError(field) {
      return !!this.formErrors[field];
    },
    getError(field) {
      return this.formErrors[field] ? this.formErrors[field][0] : "";
    },
    removeError(field) {
      if (this.formErrors[field]) {
        delete this.formErrors[field];
      }
      this.formLevelError = "";
    },
    async fetchHotels() {
      try {
        const response = await request({ method: 'get', url: '/hotels' });
        this.hotelOptions = (response.data || []).map(hotel => ({
          id: String(hotel.id),
          label: hotel.name,
        }));
      } catch (e) {
        this.hotelOptions = [];
      }
    },
    async fetchFacilities() {
      try {
        const response = await request({ method: 'get', url: '/facilities' });
        this.facilityOptions = (response.data || []).map(facility => ({
          id: String(facility.id),
          name: facility.name,
        }));
      } catch (e) {
        this.facilityOptions = [];
      }
    },
    async fetchHotelFacility() {
      try {
        const response = await request({
          method: "get",
          url: `hotel-facilities/${this.id}`,
        });
        this.formFields.hotel_id = String(response.hotel.id);
        this.formFields.facility_id = Array.from(response.facility_ids, String);
      } catch (error) {
        this.formLevelError = error.response?.data?.message || this.$t("hotel_facilities.fetch_error");
        this.notifyError(this.formLevelError);
        this.$router.push("/admin/hotel-facilities");
      }
    },
    async handleSubmit() {
      try {
        this.validated = true;
        this.isSubmitting = true;
        this.formErrors = {};
        this.formLevelError = "";

        if (!this.formFields.hotel_id) {
          this.formErrors.hotel_id = [this.$t("hotel_facilities.validation.hotel_required")];
        }
        if (!this.formFields.facility_id.length) {
          this.formErrors.facility_id = [this.$t("hotel_facilities.validation.facility_required")];
        }

        if (Object.keys(this.formErrors).length > 0) {
          throw new Error("Validation failed");
        }

        await request({
          method: this.isEdit ? "put" : "post",
          url: this.isEdit ? `/hotel-facilities/${this.id}` : "/hotel-facilities",
          data: this.formFields,
        });
        this.notifySuccess(
          this.isEdit ? this.$t("hotel_facilities.update_success") : this.$t("hotel_facilities.create_success")
        );
        this.$router.push("/admin/hotel-facilities");
      } catch (error) {
        if (error.status === 422) {
          this.formErrors = error.data.message || {};
          this.formLevelError = this.formErrors;
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    cancel() {
      this.$router.push("/admin/hotel-facilities");
    },
  },
  created() {
    this.fetchHotels();
    this.fetchFacilities();
    if (this.isEdit) {
      this.fetchHotelFacility();
    }
  },
};
</script>

<style scoped>
.hotel-facility-form {
  padding: 20px;
}
</style>
